import React, { useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Splide, SplideSlide } from '@splidejs/react-splide';
import classnames from 'classnames';
import Mux from '@mux/mux-player-react';
import { InView } from 'react-intersection-observer';

import { isLessThanBreakpoint } from 'dpl/util/grid';
import Icon from 'dpl/common/components/Icon';
import { FUTURE_BREEDER_VIDEOS } from 'dpl/homepage/utils/constants';

const IS_LT_MD = isLessThanBreakpoint('md');

function BreederVideoCard({ breederVideo, isHovered }) {
  const { muxPlaybackId, breederSlug, breederName, breedName } = breederVideo;
  const videoRef = useRef(null);

  const PLAY_BTN_SIZE = '48px';
  const hidePlayBtn = Boolean(isHovered);

  return (
    <>
      <div
        className="BreederVideoCard br4 w-100 relative overflow-hidden flex flex-column gap-32 md:gap-40 lg:gap-40 mb2"
        style={{ height: '458px' }}
      >
        <Mux
          paused={!isHovered}
          play={isHovered}
          ref={videoRef}
          playbackId={muxPlaybackId}
          preload="metadata"
          nohotkeys
          thumbnailTime={0}
          loop
          controls={false}
          accentColor="rgba(4 4 21 / 60%)"
          autoPlay={isHovered}
          style={{
            '--bottom-controls': 'none',
            '--center-seek-forward-button': 'none',
            '--center-seek-backward-button': 'none',
            '--bottom-play-button': 'none',
            '--bottom-volume-range': 'none',
            '--bottom-time-range:': 'none',
            '--bottom-time-display': 'none',
            '--bottom-duration-display': 'none',
            '--bottom-mute-button': 'none',
            '--bottom-captions-button': 'none',
            '--bottom-airplay-button': 'none',
            '--bottom-pip-button': 'none',
            '--bottom-playback-rate-button': 'none',
            '--center-play-button': 'none',
            '--bottom-cast-button': 'none',
            '--media-object-fit': 'cover',
            pointerEvents: 'none',
            width: '100%',
            height: '100%'
          }}
          muted
        />
        {!hidePlayBtn && (
          <div className="transform-center-all pointer">
            <Icon
              width={PLAY_BTN_SIZE}
              height={PLAY_BTN_SIZE}
              name="fetch-play"
              className="white bg-black-60 br-100 ph3 pv3"
            />
          </div>
        )}
      </div>
      <a
        className={classnames('font-16 md:font-14 lg:font-14 fw-medium white', {
          'margin-b-2': !IS_LT_MD
        })}
        href={`/breeders/${breederSlug}`}
        target="_blank"
        rel="noopener noreferrer"
      >
        {breederName}
      </a>
      <p className="font-14 white">Raising {breedName}</p>
    </>
  );
}

BreederVideoCard.propTypes = {
  breederVideo: PropTypes.shape({
    muxPlaybackId: PropTypes.string,
    breederSlug: PropTypes.string,
    breederName: PropTypes.string,
    breedName: PropTypes.string
  }).isRequired,
  isHovered: PropTypes.bool
};

BreederVideoCard.defaultProps = {
  isHovered: false
};

export default function FutureBreederVideosSection() {
  const splideRef = useRef(null);
  const [activeIndex, setActiveIndex] = useState(0);
  const [cardsPerPage, setCardsPerPage] = useState(4);
  const [hoveredIndex, setHoveredIndex] = useState(null);

  function handleActiveSlide(slide) {
    setActiveIndex(slide.index);

    if (IS_LT_MD) {
      setHoveredIndex(slide.index);
    }
  }

  function handleClick(idx) {
    if (hoveredIndex !== null) {
      setHoveredIndex(null);
    } else {
      setHoveredIndex(idx);
    }
  }

  function handleResize(splide) {
    setCardsPerPage(splide.options.perPage);
  }

  function handleMouseEnter(idx) {
    if (IS_LT_MD) return;
    setHoveredIndex(idx);
  }

  function handleInViewChange(inView) {
    if (IS_LT_MD && inView) {
      setHoveredIndex(activeIndex);
    }

    if (!inView) {
      setHoveredIndex(null);
    }
  }

  const breedersCount = FUTURE_BREEDER_VIDEOS.length;

  const isNextButtonDisabled = activeIndex >= breedersCount - cardsPerPage;
  const isPrevButtonDisabled = activeIndex <= 0;

  return (
    <section className="FutureBreederVideosSection bg-stone-900 white section-vertical-padding">
      <div className="container-xl section-fade">
        <div className="flex flex-column items-center tc-ns mb10 mb12-md">
          <h3 className="font-28 md:font-40 mb4 lh-title ph6-lg">
            See how your future dog is raised
          </h3>
          <p className="font-16 md:font-18 w-60-md w-40-lg">
            Good Dog breeders share how they raise their dogs, so you can get to
            know them and their practices.
          </p>
        </div>
        <div className="BreederVideosCarousel">
          <Splide
            ref={splideRef}
            className="overflow-hidden-ns"
            onActive={handleActiveSlide}
            onMounted={handleResize}
            onResized={handleResize}
            options={{
              arrows: false,
              drag: breedersCount > 0,
              dragMinThreshold: 20,
              easing: 'cubic-bezier(0.5, 0, 0.5, 1)',
              flickMaxPages: cardsPerPage,
              flickPower: 300,
              gap: IS_LT_MD ? 16 : 24,
              keyboard: 'focused',
              pagination: false,
              perPage: cardsPerPage,
              speed: IS_LT_MD ? 350 : 700,
              snap: true,
              start: activeIndex,
              breakpoints: {
                1200: { perPage: 4 },
                960: { perPage: 3 },
                720: { perPage: 2 },
                540: { perPage: 1 }
              }
            }}
          >
            {FUTURE_BREEDER_VIDEOS.map((breederVideo, idx) => (
              <SplideSlide
                key={`${breederVideo.breederName}-${idx}`}
                onMouseEnter={() => handleMouseEnter(idx)}
                onClick={() => handleClick(idx)}
              >
                <InView onChange={handleInViewChange} threshold={0.75}>
                  <BreederVideoCard
                    breederVideo={breederVideo}
                    isHovered={idx === hoveredIndex}
                  />
                </InView>
              </SplideSlide>
            ))}
          </Splide>
        </div>
        <div className="mt10 mt12-md relative w-100" style={{ height: 2 }}>
          <span className="absolute bg-purple-500 db o-50 h-100 w-100" />
          <span
            className="absolute bg-purple-100 db h-100 transition"
            style={{
              width: `${(100 / breedersCount) * (activeIndex + cardsPerPage)}%`
            }}
          />
        </div>
        <div className="dn db-ns mt6 secondary">
          <button
            type="button"
            className={classnames('bg-white ba b--default br-100 mr4', {
              'o-90': !isPrevButtonDisabled,
              'o-40': isPrevButtonDisabled
            })}
            disabled={isPrevButtonDisabled}
            onClick={() => splideRef.current.go('<')}
          >
            <div className="flex items-center justify-center ph3 pv3">
              <Icon name="fetch-arrow-left-md" />
            </div>
          </button>
          <button
            type="button"
            className={classnames('bg-white ba b--default br-100', {
              'o-90': !isNextButtonDisabled,
              'o-40': isNextButtonDisabled
            })}
            disabled={isNextButtonDisabled}
            onClick={() => splideRef.current.go('>')}
          >
            <div className="flex items-center justify-center ph3 pv3">
              <Icon name="fetch-arrow-right-md" />
            </div>
          </button>
        </div>
      </div>
    </section>
  );
}
